import {
  Box,
  Center,
  Fieldset,
  Group,
  HStack,
  Input,
  Spacer,
  Stack,
  StackSeparator,
  VisuallyHidden
} from '@chakra-ui/react'
import set from 'lodash/set'
import type { FC, FormEventHandler } from 'react'
import { useCallback, useState } from 'react'
import Img from 'react-cloudimage-responsive'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { FileUploadDropzone, FileUploadRoot } from '@app/components/ui/file-upload'
import { Skeleton } from '@app/components/ui/skeleton'
import { toaster } from '@app/components/ui/toaster'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import FormAlert from '@app/next/forms/formAlert'
import ToggleInput from '@app/next/forms/toggleInput'
import UppyRoot from '@app/next/forms/uppyRoot'
import StatIntervals from '@app/pages/settings/organization/components/statIntervals'
import unpackErrors from '@app/utils/unpackErrors'
import { useOrganizationUpdateMutation } from '@graphql/queries'
import type { OrganizationUpdateInput } from '@graphql/types'

interface Props {
  uploading?: boolean
  logo?: string
}

const LogoPreview: FC<Props> = ({ uploading = false, logo = null }) => {
  let rendered = null

  if (!logo) {
    return null
  }

  if (uploading) {
    rendered = <Skeleton w="full" h="256px" />
  } else {
    const params = { w: '230', h: '230', org_if_sml: '1', func: 'bound' }

    rendered = (
      <Center h="256px">
        <Img params={params} src={logo} alt="logo" lazyLoading={false} doNotReplaceURL />
      </Center>
    )
  }

  return (
    <Box w="full" h="256px" bg="bg" borderWidth={1} borderColor="border">
      {rendered}
    </Box>
  )
}

const OrganizationForm = ({ organization }) => {
  const [uploading, setUploading] = useState(false)
  const [errors, setErrors] = useState<ReturnType<typeof unpackErrors>>({})

  const googleSheet = useGetObjectsByProperties('googleSheet', { organizationLevel: true })?.[0]
  const [, updateOrganization] = useOrganizationUpdateMutation()

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault()
      const form = e.currentTarget
      const data = new FormData(form)
      const entries = Array.from(data.entries())

      const input: OrganizationUpdateInput = {
        organizationId: organization?.id
      }

      for (const [key, value] of entries) {
        set(input, key, value)
      }

      try {
        const response = await updateOrganization({ input })

        const resultErrors = response?.data?.organizationUpdate?.errors
        if (resultErrors && resultErrors.length) {
          setErrors(unpackErrors(resultErrors))

          form.reset()
        } else {
          toaster.create({
            title: 'Organization updated',
            description: 'Your organization has been updated successfully!',
            type: 'success'
          })
        }
      } catch (err) {
        setErrors(unpackErrors([err]))
      }
    },
    [organization?.id, updateOrganization]
  )

  return (
    <Stack gap={{ base: 4, sm: 6 }} separator={<StackSeparator />}>
      <form onSubmit={onSubmit} id="organization-form">
        <VisuallyHidden asChild>
          <Input defaultValue={organization?.id} name="organizationId" />
        </VisuallyHidden>
        <Fieldset.Root
          borderWidth={1}
          borderStyle="solid"
          borderColor="border.default"
          borderRadius="md"
          px={6}
          p={6}
          bg="bg"
        >
          <Fieldset.Content>
            <FormAlert description={errors?.global?.message} title="Failed to update your organization!" />
            <Stack gap="5">
              <Field label="Name" required invalid={!!errors?.name} errorText={errors?.name?.message}>
                <Input defaultValue={organization?.name} name="name" placeholder="Acme Co." />
              </Field>
              <Field label="Homepage URL" invalid={!!errors?.homepageUrl} errorText={errors?.homepageUrl?.message}>
                <Input defaultValue={organization?.homepageUrl} name="homepageUrl" placeholder="https://acme.co" />
              </Field>
              <UppyRoot
                fileTypes={['image/gif', 'image/jpeg', 'image/png']}
                onUploadStart={() => setUploading(true)}
                onUploadSuccess={() => setUploading(false)}
                onUploadError={() => setUploading(false)}
              >
                {({ uploading: logoUploading, shrineHash, onFileAccept }) => (
                  <Field label="Logo">
                    <HStack w="full">
                      <FileUploadRoot
                        gap="1"
                        onFileAccept={onFileAccept}
                        maxW="xl"
                        alignItems="stretch"
                        maxFiles={1}
                        accept={['image/gif', 'image/jpeg', 'image/png']}
                      >
                        <VisuallyHidden asChild>
                          <Input name="logo" readOnly value={shrineHash} />
                        </VisuallyHidden>
                        <FileUploadDropzone
                          label="Drag and drop or click here to upload a logo"
                          description=".png, .jpg up to 10MB"
                        />
                      </FileUploadRoot>
                      <Spacer />
                      <LogoPreview logo={organization?.logo} uploading={logoUploading} />
                    </HStack>
                  </Field>
                )}
              </UppyRoot>

              <Field label="Preferred data import time">
                <Input
                  defaultValue={organization?.defaultImportTime || '08:00'}
                  name="defaultImportTime"
                  pattern="[0-9]{1,2}:[0-9]{2}(\s?[aApP]\.?[mM]\.?)?"
                  type="time"
                />
              </Field>

              <Field label="Default statistic intervals">
                <Stack w="full">
                  <StatIntervals organization={organization} errors={errors} />
                </Stack>
              </Field>

              {googleSheet && (
                <Field
                  label="Auto-import Google sheet data"
                  invalid={!!errors?.autoImportGoogleSheet}
                  errorText={errors?.autoImportGoogleSheet?.message}
                >
                  <ToggleInput name="autoImportGoogleSheet" defaultValue={organization?.autoImportGoogleSheet} />
                </Field>
              )}
            </Stack>
            <Group justifyContent="end">
              <Button disabled={uploading} type="submit">
                Save settings
              </Button>
            </Group>
          </Fieldset.Content>
        </Fieldset.Root>
      </form>
    </Stack>
  )
}

export default OrganizationForm
